import { memo, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import MultipleIndicatorFilters from 'pages/PlanPageDetailsOPZ/Plan/Filters/MultipleIndicatorFilters';
import {
  getBlockDetails,
  getBlockMetadata,
  groupByFilterChange,
  overallGroupByFilter,
  setEmptyData,
} from 'redux/PlanPageSlice';
import { getCompareScenario } from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import {
  PLAN_FILTER_BADGE_BG,
  PLAN_FILTER_PILL_HEIGHT,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
} from '../planPageRevampUi';
import { PlanToolbarChevronDownIcon, PlanToolbarGroupByIcon } from '../icons';

interface Properties {
  refetchRequest: () => void;
}

const PlanFilterGroupPill = ({ refetchRequest }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const metadata = useSelector(getBlockMetadata);
  const blockDetails = useSelector(getBlockDetails);
  const selectedGroupBy = useSelector(overallGroupByFilter);
  const compareScenario = useSelector(getCompareScenario);
  const themeColors = useSelector(getPlanPageThemeColors);

  const dimensions = useMemo(() => metadata?.dimensions || [], [metadata?.dimensions]);
  const groupByLabel = useMemo(() => {
    const selectedDimension = dimensions.find(
      (dimension: any) => String(dimension.id) === String(selectedGroupBy),
    );
    return selectedDimension?.name ?? 'Total';
  }, [dimensions, selectedGroupBy]);

  const handleGroupByChange = useCallback(
    (value: string | number) => {
      if (String(selectedGroupBy) === String(value)) return;
      dispatch(setEmptyData(true));
      dispatch(groupByFilterChange(value));

      if (blockDetails?.model_id && blockDetails?.id) {
        const planFilters = localStorage.getItem('global-modal-filters');
        const data = planFilters ? JSON.parse(planFilters) : {};
        const modalFilters = data[blockDetails.model_id] || {};
        modalFilters[blockDetails.id] = { groupBy: value };
        localStorage.setItem(
          'global-modal-filters',
          JSON.stringify({ ...data, [blockDetails.model_id]: modalFilters }),
        );
      }
    },
    [blockDetails?.id, blockDetails?.model_id, dispatch, selectedGroupBy],
  );

  return (
    <Flex
      h={PLAN_FILTER_PILL_HEIGHT}
      alignItems='center'
      gap={3}
      px={3}
      py='7px'
      border='1px solid'
      borderColor={themeColors.border}
      borderRadius={PLAN_REVAMP_RADIUS_SM}
      bg='white'
      overflow='hidden'
      flexShrink={0}
    >
      <MultipleIndicatorFilters
        variant='revamp'
        embedded
        badgeColor={PLAN_FILTER_BADGE_BG}
        onApplyFilters={refetchRequest}
      />

      <Divider orientation='vertical' h='22px' borderColor={themeColors.border} flexShrink={0} />

      <Box flexShrink={0}>
        <Menu>
          <MenuButton
            as={Button}
            size='sm'
            h={PLAN_FILTER_PILL_HEIGHT}
            minH={PLAN_FILTER_PILL_HEIGHT}
            px={0}
            py={0}
            w='fit-content'
            {...PLAN_TEXT.body}
            border='none'
            borderRadius='0'
            bg='transparent'
            color={themeColors.textDark}
            display='inline-flex'
            alignItems='center'
            flexWrap='nowrap'
            gap={2}
            lineHeight='1'
            boxShadow='none'
            cursor={compareScenario ? 'not-allowed' : 'pointer'}
            opacity={compareScenario ? 0.5 : 1}
            pointerEvents={compareScenario ? 'none' : 'auto'}
            _hover={{ bg: themeColors.activeBg }}
            _expanded={{ bg: themeColors.activeBg }}
            _focus={{ boxShadow: 'none' }}
          >
            <Flex alignItems='center' gap={3} flexWrap='nowrap' minW={0}>
              <Box flexShrink={0} display='flex' alignItems='center'>
                <PlanToolbarGroupByIcon width={16} height={16} color={themeColors.primary} />
              </Box>
              <Text
                as='span'
                {...PLAN_TEXT.body}
                whiteSpace='nowrap'
                lineHeight='1.2'
                noOfLines={1}
                maxW='220px'
                minW={0}
              >
                Group by : {groupByLabel}
              </Text>
              <Box flexShrink={0} display='flex' alignItems='center'>
                <PlanToolbarChevronDownIcon width={10} height={6} color={themeColors.primary} />
              </Box>
            </Flex>
          </MenuButton>
          <MenuList zIndex={1500} minW='180px' py={1} borderRadius='10px' boxShadow='md'>
            <MenuItem
              {...(selectedGroupBy === 'Total' ? PLAN_TEXT.bodySemibold : PLAN_TEXT.body)}
              color={selectedGroupBy === 'Total' ? themeColors.primary : themeColors.textDark}
              bg={selectedGroupBy === 'Total' ? themeColors.activeBg : 'transparent'}
              _hover={{ bg: themeColors.activeBg }}
              onClick={() => handleGroupByChange('Total')}
            >
              Total
            </MenuItem>
            {dimensions.map((dimension: any) => (
              <MenuItem
                key={dimension.id}
                {...(String(dimension.id) === String(selectedGroupBy)
                  ? PLAN_TEXT.bodySemibold
                  : PLAN_TEXT.body)}
                color={
                  String(dimension.id) === String(selectedGroupBy)
                    ? themeColors.primary
                    : themeColors.textDark
                }
                bg={
                  String(dimension.id) === String(selectedGroupBy)
                    ? themeColors.activeBg
                    : 'transparent'
                }
                _hover={{ bg: themeColors.activeBg }}
                onClick={() => handleGroupByChange(dimension.id)}
              >
                {dimension.name}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Box>
    </Flex>
  );
};

export default memo(PlanFilterGroupPill);
